import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Avatar,
  Typography,
} from "@mui/material";
import { countryCodes, CountryOption } from "../data/countryCodes";

const sortedOptions: CountryOption[] = [
  countryCodes.find((c) => c.iso === "CA")!,
  ...countryCodes
    .filter((c) => c.iso !== "CA")
    .sort((a, b) => a.label.localeCompare(b.label)),
];

interface CountryCodeDropdownProps {
  onChange: (code: string) => void;
}

const CountryCodeDropdown: React.FC<CountryCodeDropdownProps> = ({ onChange }) => {
  const [selected, setSelected] = useState<CountryOption | null>(
    sortedOptions[0]
  );

  React.useEffect(() => {
    // Send initial country code
    if (selected) {
      onChange(selected.code);
    }
  }, [selected, onChange]);

  return (
    <Autocomplete
      options={sortedOptions}
      value={selected}
      onChange={(_, newValue) => {
        setSelected(newValue);
        if (newValue) {
          onChange(newValue.code);
        }
      }}
      isOptionEqualToValue={(option, value) =>
        option.code === value.code && option.iso === value.iso
      }
      getOptionLabel={(option) =>
        option ? `${option.label} (${option.code})` : ""
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={`${option.code}-${option.iso}`}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Avatar
            src={`https://flagcdn.com/w40/${option.iso.toLowerCase()}.png`}
            alt={option.iso}
            sx={{ width: 24, height: 24 }}
          />
          <Typography variant="body2">
            {option.label} ({option.code})
          </Typography>
        </Box>
      )}
      renderInput={(params) => {
        const flagUrl = selected
          ? `https://flagcdn.com/w40/${selected.iso.toLowerCase()}.png`
          : "";
        return (
          <TextField
            {...params}
            label="Select Country Code"
            InputProps={{
              ...params.InputProps,
              startAdornment: selected && (
                <Avatar
                  src={flagUrl}
                  alt={selected.iso}
                  sx={{ width: 24, height: 24, mr: 1 }}
                />
              ),
            }}
          />
        );
      }}
    />
  );
};

export default CountryCodeDropdown;
